/**
 * coloma-mqtt — store Zustand que consume el estado de los reguladores de
 * Santa Coloma vía MQTT (broker de ARS93).
 *
 * Es un store global (no un hook con estado local) porque `useIntersectionState`
 * lo consulta una vez por regulador (~78 veces) y no debe abrir 78 conexiones.
 * Mismo patrón que `useRegulatorAPI`.
 *
 * Recepción de estado de los reguladores y envío de comandos (`sendCommand`),
 * con historial correlacionado por `request_id` vía el topic `.../result`.
 */

import { create } from 'zustand';
import mqtt from 'mqtt';
import type {
  ReguladorStatus,
  GrupoStatus,
  MatrizFaseEntry,
  LabCommand,
  LabResult,
  CommandLogEntry,
} from '../types/regulador-mqtt';

const MQTT_URL = import.meta.env.VITE_MQTT_URL || 'ws://localhost:9001';
const MQTT_USER = import.meta.env.VITE_MQTT_USER || 'scg-frontend';
const MQTT_PASS = import.meta.env.VITE_MQTT_PASS || '';

// Namespace de topics de Santa Coloma: santacoloma/<device>/<tipo>
const TOPIC_BASE = 'santacoloma';
const TOPIC_STATUS = `${TOPIC_BASE}/+/status`;
const TOPIC_RESULT = `${TOPIC_BASE}/+/result`;

// Sin status en este intervalo → el regulador se marca offline.
const STALE_MS = 20_000;

// Tope defensivo del store: no aceptamos más de N reguladores distintos.
// Santa Coloma tiene ~79; un margen amplio protege contra una inundación
// de IDs nuevos por un publicador comprometido sin afectar la operación normal.
const MAX_REGULADORES = 500;

// Solo aceptamos IDs canónicos del namespace (`SCG` + 3-4 dígitos).
const ID_PATTERN = /^SCG\d{3,4}$/;

interface ColomaMqttState {
  /** true mientras el cliente MQTT está conectado al broker. */
  connected: boolean;
  /** Estado de cada regulador, indexado por su ID canónico (payload.id). */
  reguladores: Record<string, ReguladorStatus>;
  /** Historial de comandos enviados (más reciente primero). */
  commandLog: CommandLogEntry[];

  connect: () => void;
  disconnect: () => void;
  /** Publica un comando a un regulador. Devuelve el request_id. */
  sendCommand: (
    canonicalId: string,
    cmd: string,
    params?: Record<string, number | string>,
  ) => string;
}

/** Normaliza un array de grupos semafóricos al contrato GrupoStatus. */
export function sanitizeGrupos(arr: unknown): GrupoStatus[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((g): g is Record<string, unknown> => !!g && typeof g === 'object')
    .map(g => ({
      grupo: typeof g.grupo === 'number' ? g.grupo : 0,
      estado_une: typeof g.estado_une === 'number' ? g.estado_une : 0,
      label: typeof g.label === 'string' ? g.label : '',
    }));
}

// Margen de tolerancia para desfase de reloj razonable entre el emisor y
// este cliente — más allá de esto se trata como corrupto/manipulado.
const FUTURE_TOLERANCE_MS = 5 * 60_000;

/** Un `ultima_lectura` no parseable o futuro (reloj del emisor corrupto o
 * manipulado) rompía el mecanismo de staleness: `age = now - Date.parse(...)`
 * da NaN o negativo, y el regulador nunca se marca offline sin importar
 * cuánto lleve sin publicar de verdad. Si no es una fecha válida y no-futura,
 * se trata como recibido ahora mismo. */
export function sanitizeUltimaLectura(value: unknown): string {
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed) && parsed <= Date.now() + FUTURE_TOLERANCE_MS) {
      return value;
    }
  }
  return new Date().toISOString();
}

/**
 * Normaliza un payload `status` recibido del broker al contrato ReguladorStatus.
 * El broker entrega datos de fuentes externas (reguladores, gateways) que
 * pueden no cumplir el contrato; un payload mal formado no debe romper el
 * dashboard. Se sanea aquí, en la frontera: todo lo de dentro confía en los
 * tipos. Devuelve null si es irrecuperable (sin `id`).
 */
export function sanitizeStatus(raw: unknown): ReguladorStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !ID_PATTERN.test(r.id)) return null;

  // Detalle de ciclo (opcional): matriz de fases, tiempos y nº de grupos.
  const matrizFases: MatrizFaseEntry[] | undefined = Array.isArray(r.matriz_fases)
    ? r.matriz_fases
        .filter((f): f is Record<string, unknown> => !!f && typeof f === 'object')
        .map(f => ({
          fase: typeof f.fase === 'string' ? f.fase : String(f.fase ?? ''),
          indice: typeof f.indice === 'number' ? f.indice : 0,
          grupos: sanitizeGrupos(f.grupos),
        }))
    : undefined;

  let tiemposFase: Record<string, number> | undefined;
  if (r.tiempos_fase && typeof r.tiempos_fase === 'object' && !Array.isArray(r.tiempos_fase)) {
    tiemposFase = {};
    for (const [k, v] of Object.entries(r.tiempos_fase as Record<string, unknown>)) {
      if (typeof v === 'number') tiemposFase[k] = v;
    }
  }

  return {
    id: r.id,
    nombre: typeof r.nombre === 'string' ? r.nombre : r.id,
    online: r.online === true,
    estado: r.estado == null ? '' : String(r.estado),
    plan: typeof r.plan === 'number' ? r.plan : null,
    fase: typeof r.fase === 'string' || typeof r.fase === 'number' ? r.fase : null,
    alarmas: Array.isArray(r.alarmas)
      ? r.alarmas.filter((a): a is string => typeof a === 'string')
      : [],
    grupos: sanitizeGrupos(r.grupos),
    matriz_fases: matrizFases,
    tiempos_fase: tiemposFase,
    num_grupos: typeof r.num_grupos === 'number' ? r.num_grupos : undefined,
    ultima_lectura: sanitizeUltimaLectura(r.ultima_lectura),
  };
}

let client: mqtt.MqttClient | null = null;
let staleTimer: ReturnType<typeof setInterval> | null = null;

export const useColomaMqtt = create<ColomaMqttState>((set, get) => ({
  connected: false,
  reguladores: {},
  commandLog: [],

  connect: () => {
    if (client) return;

    client = mqtt.connect(MQTT_URL, {
      username: MQTT_USER,
      password: MQTT_PASS,
      reconnectPeriod: 5000,
      connectTimeout: 8000,
      keepalive: 30,
      clientId: `scg-front-${crypto.randomUUID().slice(0, 8)}`,
    });

    client.on('connect', () => {
      set({ connected: true });
      client?.subscribe([TOPIC_STATUS, TOPIC_RESULT], { qos: 1 });
    });

    client.on('message', (topic, payload) => {
      let data: unknown;
      try {
        data = JSON.parse(payload.toString());
      } catch {
        return;
      }
      if (topic.endsWith('/status')) {
        const status = sanitizeStatus(data);
        if (!status) return; // payload irrecuperable → se descarta
        // ARS-02: el retained que el broker entrega al conectar puede traer
        // online:true de hace días (p. ej. si el proceso murió sin disparar
        // el LWT). Evaluar la antigüedad aquí, no solo en el timer de
        // staleness (cada 2s) — si no, ese regulador se pinta en verde un
        // instante hasta el próximo tick.
        if (status.online && Date.now() - Date.parse(status.ultima_lectura) > STALE_MS) {
          status.online = false;
        }
        set(s => {
          // Tope del store: si está lleno y el ID es nuevo, hay que hacer
          // hueco. Descartar el nuevo (comportamiento anterior) dejaba que
          // una inundación de IDs falsos con retain=true bloqueara el hueco
          // para siempre — sobreviven a cada reconexión. En vez de eso se
          // desaloja el regulador con la lectura más antigua: un publicador
          // real que empieza a publicar siempre acaba desplazando basura
          // estática, nunca al revés.
          if (!(status.id in s.reguladores)
              && Object.keys(s.reguladores).length >= MAX_REGULADORES) {
            const entries = Object.entries(s.reguladores);
            let oldestId = entries[0][0];
            let oldestTs = Date.parse(entries[0][1].ultima_lectura);
            for (const [id, r] of entries) {
              const ts = Date.parse(r.ultima_lectura);
              if (ts < oldestTs) { oldestTs = ts; oldestId = id; }
            }
            const next = { ...s.reguladores };
            delete next[oldestId];
            next[status.id] = status;
            return { reguladores: next };
          }
          return { reguladores: { ...s.reguladores, [status.id]: status } };
        });
      } else if (topic.endsWith('/result')) {
        const result = data as LabResult;
        if (!result || typeof result.request_id !== 'string') return;
        // `error` viene de un payload MQTT externo (regulador de campo) —
        // si no es string, React lo rompe al renderizarlo como hijo directo
        // (ControlPanel.tsx), tumbando toda la app (solo hay un
        // ErrorBoundary de nivel raíz). Sanear como el resto de campos.
        const safeError = typeof result.error === 'string' && result.error
          ? result.error
          : result.error ? 'Error no especificado' : undefined;
        set(s => ({
          commandLog: s.commandLog.map(e =>
            e.id === result.request_id
              ? { ...e, status: result.success ? 'success' : 'error', error: safeError }
              : e,
          ),
        }));
      }
    });

    client.on('close', () => set({ connected: false }));
    client.on('error', () => { /* el cliente reconecta solo */ });

    // Staleness: marca offline los reguladores que dejan de publicar.
    staleTimer = setInterval(() => {
      const now = Date.now();
      const regs = get().reguladores;
      let changed = false;
      const next: Record<string, ReguladorStatus> = {};
      for (const [id, r] of Object.entries(regs)) {
        const age = now - Date.parse(r.ultima_lectura);
        if (r.online && age > STALE_MS) {
          next[id] = { ...r, online: false };
          changed = true;
        } else {
          next[id] = r; // misma referencia → no re-renderiza ese regulador
        }
      }
      if (changed) set({ reguladores: next });
    }, 2000);
  },

  disconnect: () => {
    if (staleTimer) {
      clearInterval(staleTimer);
      staleTimer = null;
    }
    if (client) {
      client.end(true);
      client = null;
    }
    set({ connected: false });
  },

  sendCommand: (canonicalId, cmd, params = {}) => {
    const requestId = crypto.randomUUID();
    const device = canonicalId.toLowerCase();
    const timestamp = new Date().toISOString();
    const message: LabCommand = { cmd, params, request_id: requestId, timestamp };
    client?.publish(`${TOPIC_BASE}/${device}/cmd`, JSON.stringify(message), { qos: 1 });
    const entry: CommandLogEntry = {
      id: requestId, canonicalId, cmd, params, status: 'pending', timestamp,
    };
    set(s => ({ commandLog: [entry, ...s.commandLog].slice(0, 50) }));
    return requestId;
  },
}));
